import { Edit, Loader2, MoreHorizontal, Trash } from "lucide-react";
import type React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useClickOutside } from "@/hooks/useClickOutside";

interface CommentHeaderActionsProps {
  commentId: number;
  isCurrentUserAuthor?: boolean;
  isAnyActionLoading?: boolean;
  isDeleting?: boolean;
  onEditClick: (commentId: number) => void;
  onDeleteClick: (commentId: number) => void;
}

export const CommentHeaderActions: React.FC<CommentHeaderActionsProps> = ({
  commentId,
  isCurrentUserAuthor = false,
  isAnyActionLoading = false,
  isDeleting = false,
  onEditClick,
  onDeleteClick,
}) => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useClickOutside(() => setShowMenu(false), menuRef, menuButtonRef);

  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowMenu((prev) => !prev);
  };

  const createMenuClickHandler =
    (handler: (commentId: number) => void) => (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setShowMenu(false);
      handler(commentId);
    };

  if (!isCurrentUserAuthor) {
    return null;
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        onClick={toggleMenu}
        ref={menuButtonRef}
        disabled={isAnyActionLoading}
        className="size-8 p-0"
      >
        <MoreHorizontal size={16} />
      </Button>

      {showMenu && !isAnyActionLoading && (
        <div
          ref={menuRef}
          className="absolute right-0 top-full z-50 mt-1 w-28 rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          <Button
            variant="ghost"
            className="h-8 w-full justify-start text-sm"
            onClick={createMenuClickHandler(onEditClick)}
          >
            <Edit size={14} className="mr-2" /> {t("common.edit")}
          </Button>
          <Button
            variant="ghost"
            className="h-8 w-full justify-start text-sm"
            onClick={createMenuClickHandler(onDeleteClick)}
            disabled={isAnyActionLoading}
          >
            {isDeleting ? (
              <Loader2 size={14} className="mr-2 animate-spin" />
            ) : (
              <Trash size={14} className="mr-2" />
            )}
            {t("common.delete")}
          </Button>
        </div>
      )}
    </div>
  );
};
